import { createAction, createAsyncThunk, createReducer } from "@reduxjs/toolkit";

import { fetchWithAuth } from "~/common/fetch";
import { API_EXTRA_SERVICES } from "~/config";
import type { RootState } from "~/store";
import type { TPatient } from "~/types/patient";
import type { MutableQueryState } from "~/types/redux";
import type { TService } from "~/types/service";

const initialState: MutableQueryState<TService[]> = {
  status: "idle",
  data: undefined,
  error: undefined,
};

export const clearExtraServices = createAction("CLEAR_EXTRA_SERVICES");

export const getExtraServices = createAsyncThunk(
  "services/fetchByPatientId",
  async (patient_id: TPatient["patient_id"]) => {
    const response = await fetchWithAuth(API_EXTRA_SERVICES(patient_id));

    if (!response.ok) {
      throw new Error(`Failed loading {GET_EXTRA_SERVICES}: ${response.statusText}`);
    }

    return response.json() as Promise<TService[]>;
  },
);

const NO_SERVICES: TService[] = [];

export const selectServices = (state: RootState): TService[] => state.services.data ?? NO_SERVICES;
export const selectServicesAreLoading = (state: RootState) =>
  state.services.data == undefined && state.services.status == "loading";

export const services = createReducer(initialState, (builder) =>
  builder
    .addCase(getExtraServices.pending, (state) => {
      state.status = "loading";
    })
    .addCase(getExtraServices.fulfilled, (state, action) => {
      state.status = "success";
      state.data = action.payload;
      state.error = undefined;
    })
    .addCase(getExtraServices.rejected, (state, action) => {
      state.status = "error";
      state.data = undefined;
      state.error = action.error;
    })
    .addCase(clearExtraServices, (state) => {
      state.data = undefined;
    }),
);
